import api from './api';

/**
 * WAREHOUSE-ADMIN.1 — warehouses and grants.
 *
 * Warehouses are company-scoped but addressed by venue, mirroring the API:
 * every admin route is venue-addressed, and the venue's company is resolved
 * server-side. So these all take a venueId and return the COMPANY's warehouses.
 */

export const WAREHOUSE_TYPES = ['CELLAR', 'STOREROOM', 'WAREHOUSE', 'KITCHEN', 'BAR_STORE', 'OTHER'] as const;
export type WarehouseType = typeof WAREHOUSE_TYPES[number];

export const WAREHOUSE_PERMISSIONS = ['SELL', 'RECEIVE', 'COUNT', 'TRANSFER_OUT', 'ADJUST', 'VIEW', 'VIEW_COST'] as const;
export type WarehousePermission = typeof WAREHOUSE_PERMISSIONS[number];

/**
 * The permissions the SYSTEM ACTUALLY ENFORCES, offered as real choices.
 *
 * WAREHOUSE-LOCATIONS.1 widened this. SELL was once the only permission that
 * gated anything, so the editor deliberately offered one checkbox — showing six
 * that restrict nothing is worse than showing none, because a user who unticks
 * "receive" would believe they had limited something and be wrong.
 *
 * Now the write paths check theirs too: transfer resolves TRANSFER_OUT at the
 * source and RECEIVE at the destination, and stock counts resolve COUNT. So
 * these are choices that decide something and belong in the UI.
 *
 * Offering them is also what makes a SHARED cellar usable at all. It has no
 * home venue to fall back on, so if no venue can be granted RECEIVE, nothing
 * can ever put stock into it — the warehouse would be permanently empty.
 *
 * ADJUST and VIEW_COST are still checked by nothing and stay out.
 */
export const GRANTABLE_PERMISSIONS: Array<{
  value: WarehousePermission;
  label: string;
  help: string;
}> = [
  { value: 'SELL', label: 'Sell', help: 'Ring up stock held here on the POS.' },
  { value: 'RECEIVE', label: 'Receive', help: 'Deliver or transfer stock into it.' },
  { value: 'COUNT', label: 'Count', help: 'Run a stock count against it.' },
  { value: 'TRANSFER_OUT', label: 'Transfer out', help: 'Move stock from it to somewhere else.' },
];

/**
 * VIEW rides along with any grant: a venue that may do anything here must be
 * able to see it. It is not a separate choice because it is not a separate
 * decision.
 */
export const IMPLIED_PERMISSIONS: WarehousePermission[] = ['VIEW'];

/** Default for a new grant — the common case is a venue that sells from a cellar. */
export const ENFORCED_PERMISSIONS: WarehousePermission[] = ['SELL', 'VIEW'];

export interface WarehouseGrant {
  _id: string;
  granteeType: 'VENUE' | 'COMPANY';
  granteeId: string;
  /** Resolved server-side; null when the venue no longer exists. */
  granteeName: string | null;
  permissions: WarehousePermission[];
  ownerFilter: string[] | null;
}

export interface WarehouseLocation {
  _id: string;
  name: string;
  isDefault: boolean;
}

export interface Warehouse {
  _id: string;
  name: string;
  type: WarehouseType;
  /**
   * The venue that OWNS this warehouse, or null when it is shared across the
   * company. Ownership is permanent and carries full, unrevokable rights —
   * which is why the UI shows it as a property rather than as a grant.
   */
  homeVenueId: string | null;
  homeVenueName: string | null;
  isShared: boolean;
  locations: WarehouseLocation[];
  grants: WarehouseGrant[];
}

export interface EntitlementReach {
  warehouseId: string;
  /** True for the venue's own warehouse — implicit, never a stored grant. */
  isHome: boolean;
  permissions: WarehousePermission[];
  ownerFilter: string[] | null;
}

export interface EntitlementsGraph {
  companyId: string | null;
  venues: Array<{ venueId: string; venueName: string; reaches: EntitlementReach[] }>;
  warehouses: Array<{
    _id: string; name: string; type: WarehouseType;
    isShared: boolean; homeVenueId: string | null;
  }>;
}

const base = (venueId: string) => `/admin/venue/${venueId}/warehouses`;

const warehouseService = {
  /** Every warehouse the venue's company owns, with locations and grants. */
  list: async (venueId: string): Promise<{ companyId: string | null; warehouses: Warehouse[] }> => {
    const res = await api.get(base(venueId));
    return res.data;
  },

  /**
   * Create a warehouse. Omitting homeVenueId makes it SHARED — owned by the
   * company and reachable only by grant. This cannot be changed afterwards.
   */
  create: async (
    venueId: string,
    payload: { name: string; type: WarehouseType; homeVenueId?: string | null },
  ): Promise<Warehouse> => {
    const res = await api.post(base(venueId), payload);
    return res.data;
  },

  update: async (
    venueId: string,
    warehouseId: string,
    payload: { name?: string; type?: WarehouseType; active?: boolean },
  ): Promise<Warehouse> => {
    const res = await api.put(`${base(venueId)}/${warehouseId}`, payload);
    return res.data;
  },

  /**
   * Add a location to a warehouse.
   *
   * Stock lives at a location, never at a warehouse — balances, movements and
   * reservations are all keyed on locationId. So a warehouse with no location
   * can hold nothing and cannot be sold from. One is created automatically with
   * the warehouse; this adds further shelves, racks or rooms within it.
   */
  addLocation: async (
    venueId: string,
    warehouseId: string,
    payload: { name: string; type?: string },
  ): Promise<WarehouseLocation> => {
    const res = await api.post(`${base(venueId)}/${warehouseId}/locations`, payload);
    return res.data;
  },

  updateLocation: async (
    venueId: string,
    warehouseId: string,
    locationId: string,
    payload: { name?: string; active?: boolean },
  ): Promise<WarehouseLocation> => {
    const res = await api.put(`${base(venueId)}/${warehouseId}/locations/${locationId}`, payload);
    return res.data;
  },

  /**
   * Grant or adjust a venue's access. Upsert: re-granting the same venue
   * updates its permissions rather than adding a second grant.
   */
  setGrant: async (
    venueId: string,
    warehouseId: string,
    payload: { granteeId: string; permissions: WarehousePermission[]; ownerFilter?: string[] | null },
  ): Promise<WarehouseGrant> => {
    const res = await api.put(`${base(venueId)}/${warehouseId}/grants`, payload);
    return res.data;
  },

  revokeGrant: async (venueId: string, warehouseId: string, grantId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/${warehouseId}/grants/${grantId}`);
  },

  /**
   * The reachability graph. Asked of the server rather than derived from
   * grants, because a venue's own warehouse carries no grant row — deriving it
   * client-side would show a venue as having no access to its own stock.
   */
  entitlements: async (venueId: string): Promise<EntitlementsGraph> => {
    const res = await api.get(`${base(venueId)}/entitlements`);
    return res.data;
  },
};

export default warehouseService;

/**
 * WAREHOUSE-OVERVIEW.1 — every warehouse THIS USER can reach.
 *
 * Addressed to the user, not to a venue. Every other warehouse read here takes
 * a venueId, which for a shared cellar could not change the answer: any granted
 * venue in the slot returned the same thing.
 */
export interface WarehouseOverviewRow {
  _id: string;
  name: string;
  type: string;
  homeVenueId: string | null;
  homeVenueName: string | null;
  isShared: boolean;
  /** Owned by one of YOUR venues — "yours", as against "shared with you". */
  isOwn: boolean;
  permissions: string[];
  /** Which of your venues it comes through. Empty for a system admin. */
  viaVenueNames: string[];
  locationCount: number;
  locations: Array<{ _id: string; name: string }>;
  itemCount: number;
  units: number;
}

export interface WarehouseOverview {
  warehouses: WarehouseOverviewRow[];
  totals: { warehouses: number; locations: number; units: number };
}

export const getWarehouseOverview = async (companyId?: string): Promise<WarehouseOverview> => {
  const res = await api.get('/admin/warehouse/overview', {
    params: companyId ? { companyId } : undefined,
  });
  return res.data;
};

/**
 * WAREHOUSE-DETAIL.1 — what is in one warehouse, and what happened in it.
 * Addressed by the warehouse. Nothing here is scoped by venue: a shared cellar
 * holds several venues' stock and belongs to none of them.
 */
export interface WarehouseStockItem {
  inventoryItemId: string;
  displayName: string | null;
  shortId: string | null;
  units: number;
  byLocation: Array<{ locationId: string; locationName: string | null; units: number }>;
}

export interface WarehouseStockMovement {
  _id: string;
  type: string;
  quantity: number;
  postedAt: string;
  postedBy: string | null;
  inventoryItemId: string;
  displayName: string | null;
  locationId: string;
  locationName: string | null;
  /** Absent for a reader without stock-costs:view. */
  unitCost?: number | null;
  totalCost?: number | null;
  sourceDocument?: { type: string; id: string; number?: string | null } | null;
  notes: string | null;
}

export interface WarehouseStock {
  warehouse: {
    _id: string; name: string; type: string;
    homeVenueId: string | null; homeVenueName: string | null; isShared: boolean;
  };
  permissions: string[];
  locations: Array<{ _id: string; name: string; units: number }>;
  items: WarehouseStockItem[];
  movements: WarehouseStockMovement[];
  totals: { locations: number; items: number; units: number };
}

export const getWarehouseStock = async (warehouseId: string): Promise<WarehouseStock> => {
  const res = await api.get(`/admin/warehouse/${warehouseId}/stock`);
  return res.data;
};

/**
 * WAREHOUSE-LABEL.1 — how a warehouse reads in a list.
 *
 * `ensureVenueWarehouse` auto-names every venue's home warehouse "Main Store".
 * So within one company they collide BY CONSTRUCTION: a company with four
 * venues has four warehouses all called Main Store, and a list of them is four
 * identical rows. The warehouse's own name carries no information there. The
 * owning VENUE always does.
 *
 * This is the same rule `locationLabel` already applies to locations, and for
 * the same reason — "General Stock" is the auto-name for a first location, so
 * those collide identically. Two screens showing the same kind of thing must
 * not disagree about how to name it.
 *
 * Always prefixed when there is an owner, never only when it would otherwise be
 * ambiguous. A reader cannot tell a quiet label from a missing one, and which
 * store stock is in is material even when only one is on offer — they are
 * deciding whether it is the RIGHT one, not merely telling it apart.
 */
export const warehouseLabel = (w: {
  name: string; homeVenueName?: string | null;
}): string => (w.homeVenueName ? `${w.homeVenueName} · ${w.name}` : w.name);

/**
 * WAREHOUSE-LABEL.1 — what to say about who this belongs to, in three states.
 *
 * It used to be two — yours, or "Shared with you" — which read as a lie in the
 * two commonest cases. A SYSTEM_ADMIN reaches every warehouse through no venue
 * at all, so every row told them a company's own stores were "shared with"
 * them. And a venue-owned warehouse that is not yours was described as a
 * "Company warehouse", which is what a SHARED one is.
 */
export interface WarehouseOwnership {
  /** Short, for a chip. */
  chip: string;
  /** Longer, for the line beneath — null when the chip already says it all. */
  detail: string | null;
}

export const warehouseOwnership = (w: {
  isOwn: boolean; isShared: boolean;
  homeVenueName?: string | null; viaVenueNames?: string[];
}): WarehouseOwnership => {
  const via = w.viaVenueNames ?? [];

  // One of your own venues owns it outright.
  if (w.isOwn) return { chip: 'Yours', detail: null };

  // Company-owned: belongs to no venue, reached by grant — or, for a system
  // admin, reached because they reach everything.
  if (w.isShared) {
    return {
      chip: 'Shared',
      detail: via.length > 0 ? `shared with you via ${via.join(', ')}` : 'company cellar',
    };
  }

  // Owned by a venue that is not yours. The title already names that venue, so
  // the chip says how YOU come to be looking at it rather than repeating it.
  return via.length > 0
    ? { chip: 'Shared with you', detail: `via ${via.join(', ')}` }
    : { chip: 'Venue store', detail: null };
};

/**
 * COMPANY-CONTEXT.1 — the companies this user can reach.
 *
 * `name` is null when main-api has not given one up; `fallbackLabel` names the
 * company by its venues instead. Read them through companyLabel rather than
 * choosing at each render site.
 */
export interface ReachableCompany {
  companyId: string;
  name: string | null;
  fallbackLabel: string;
  venueCount: number;
}

export const getCompanies = async (): Promise<ReachableCompany[]> => {
  const res = await api.get('/admin/warehouse/companies');
  return res.data.companies ?? [];
};

export const companyLabel = (c: ReachableCompany): string => c.name ?? c.fallbackLabel;
